import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { CloudRain, Sun, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Carte Admin — Toggle manuel Mode Pluie Dakar
// Un admin peut forcer la surge x1.3 pendant N heures (compense les chauffeurs
// même quand Open-Meteo ne détecte pas encore la pluie, ou l'inverse).
export default function RainOverrideCard() {
  const [status, setStatus] = useState(null);
  const [override, setOverride] = useState(null);
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(2);
  const [note, setNote] = useState("");

  const authHeader = useCallback(() => {
    let token = null;
    try {
      const s = JSON.parse(sessionStorage.getItem("admin_session") || "{}");
      token = s?.token;
    } catch (_e) { /* no session */ }
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [publicRes, adminRes] = await Promise.all([
        axios.get(`${API}/weather/rain-status`),
        axios.get(`${API}/weather/manual-override`, { headers: authHeader() }),
      ]);
      setStatus(publicRes.data);
      setOverride(adminRes.data);
    } catch (e) {
      console.error("rain-status fetch failed", e);
    }
  }, [authHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggle = async (active) => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/weather/manual-override`,
        { active, duration_hours: duration, note },
        { headers: authHeader() }
      );
      toast.success(
        active
          ? `Mode Pluie activé pour ${duration}h · surge x${r.data.surge_multiplier}`
          : "Mode Pluie désactivé"
      );
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const isActive = Boolean(override?.active) && override?.until && new Date(override.until) > new Date();
  const autoActive = Boolean(status?.is_raining) && status?.source !== "manual_admin";

  return (
    <Card className="bg-[#0F1B33] border border-sky-500/40" data-testid="rain-override-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-white">
          <CloudRain className="w-4 h-4 text-sky-400" />
          Mode Pluie Dakar
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Statut actuel */}
        <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-xl bg-[#020617] border border-white/5">
          <div className="flex items-center gap-3">
            {status?.is_raining ? (
              <>
                <CloudRain className="w-6 h-6 text-sky-400" />
                <div>
                  <p className="text-sm font-bold text-white">Surge active · x{status.surge_multiplier}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest">
                    Source : {status.source === "manual_admin" ? "Admin (manuel)" : "Open-Meteo"}
                  </p>
                </div>
              </>
            ) : (
              <>
                <Sun className="w-6 h-6 text-amber-400" />
                <div>
                  <p className="text-sm font-bold text-white">Pas de pluie détectée</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest">
                    Temps clair · {status?.temperature_c ?? "—"}°C
                  </p>
                </div>
              </>
            )}
          </div>
          {isActive && (
            <span className="text-[10px] px-2 py-1 rounded-full bg-sky-500/20 text-sky-200 border border-sky-500/40 font-bold uppercase tracking-widest">
              Admin jusqu'à {new Date(override.until).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>

        {/* Form toggle */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-400">Durée</label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="bg-[#020617] border border-white/10 rounded-xl px-3 py-1.5 text-xs text-white"
              data-testid="rain-override-duration"
            >
              {[1, 2, 3, 4, 6, 12, 24].map((h) => (
                <option key={h} value={h}>{h} h</option>
              ))}
            </select>
          </div>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note interne (optionnel)"
            className="w-full bg-[#020617] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-500"
            data-testid="rain-override-note"
          />
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => toggle(true)}
            disabled={loading}
            className="flex-1 bg-sky-500 hover:bg-sky-400 text-white rounded-full font-black text-xs uppercase tracking-widest"
            data-testid="rain-override-activate-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudRain className="w-4 h-4 mr-1" />}
            Activer surge
          </Button>
          <Button
            onClick={() => toggle(false)}
            disabled={loading || (!isActive && !autoActive)}
            variant="outline"
            className="flex-1 rounded-full font-bold text-xs uppercase tracking-widest border-white/10 text-white hover:bg-white/5"
            data-testid="rain-override-deactivate-btn"
          >
            <Sun className="w-4 h-4 mr-1" />
            Désactiver
          </Button>
        </div>
        <p className="text-[10px] text-slate-500 text-center">
          Désactiver = bascule au mode auto Open-Meteo (surge si pluie détectée).
        </p>
      </CardContent>
    </Card>
  );
}
